/**
 * Theme constants shared by the no-flash boot script, the React provider, and
 * anything server-side that needs to read the preference.
 *
 * Storage is a cookie rather than localStorage on purpose: it is readable on
 * the server (for future SSR or an API that renders themed output) and it is
 * present on the very first request, whereas localStorage is not.
 */

export type Theme = "dark" | "light";

export const THEME_COOKIE = "ps_theme";
/** New visitors get dark — it is the brand's first impression. Light is opt-in. */
export const DEFAULT_THEME: Theme = "dark";

export function isTheme(v: unknown): v is Theme {
  return v === "dark" || v === "light";
}

/**
 * Inlined into <head> as a blocking script so `data-theme` is correct BEFORE
 * first paint — no flash, and no `cookies()` call in the root layout (which
 * would opt every static marketing route out of static rendering).
 *
 * Kept tiny and dependency-free; it runs on every page load, including the
 * 900-odd prerendered SEO pages. Written as a string because it must execute
 * ahead of hydration, not as part of it.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{var m=document.cookie.match(/(?:^|; )${THEME_COOKIE}=(dark|light)/);var t=m?m[1]:"${DEFAULT_THEME}";document.documentElement.dataset.theme=t;}catch(e){}})();`;

/** Persist for a year; Lax is enough — this is a display preference, not auth. */
export function writeThemeCookie(theme: Theme) {
  document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; samesite=lax`;
}

export function readThemeCookie(): Theme | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${THEME_COOKIE}=(dark|light)`));
  return m ? (m[1] as Theme) : null;
}
